import { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

/** How long a typed date settles before the listing is asked again. */
export const DATE_SETTLE_MS = 400;

export type FilterOption = { id: string; label: string; count: number };

/** What an options filter has ticked: listed options, and the one beside them. */
export type OptionsSelection = { selected: string[]; includeOther: boolean };

/**
 * Tick boxes over a column's options, each with how many it would list, plus
 * one option that stands outside them, such as nobody being assigned.
 * Searching narrows the options shown, never what is ticked.
 */
export const OptionsFilter = ({
  options,
  other,
  value,
  searchable = false,
  maxSelected = Number.POSITIVE_INFINITY,
  onChange,
}: {
  options: FilterOption[];
  other: { label: string; count: number };
  value: OptionsSelection;
  searchable?: boolean;
  /** Beyond this, no further option can be ticked. */
  maxSelected?: number;
  onChange: (value: OptionsSelection | undefined) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const shown = options.filter((option) =>
    option.label
      .toLocaleLowerCase()
      .includes(search.trim().toLocaleLowerCase()),
  );

  const toggle = (id: string, ticked: boolean) => {
    void onChange({
      ...value,
      selected: ticked
        ? [...value.selected, id]
        : value.selected.filter((selected) => selected !== id),
    });
  };

  return (
    <Stack spacing={1}>
      {searchable ? (
        <TextField
          size="small"
          label={t("listingFilters.search")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      ) : null}
      <FormGroup>
        {shown.map((option) => (
          <FormControlLabel
            key={option.id}
            control={
              <Checkbox
                size="small"
                checked={value.selected.includes(option.id)}
                disabled={
                  !value.selected.includes(option.id) &&
                  value.selected.length >= maxSelected
                }
                onChange={(event) => {
                  toggle(option.id, event.target.checked);
                }}
              />
            }
            label={t("listingFilters.option", option)}
          />
        ))}
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={value.includeOther}
              onChange={(event) => {
                void onChange({ ...value, includeOther: event.target.checked });
              }}
            />
          }
          label={t("listingFilters.option", other)}
        />
      </FormGroup>
      <ClearButton onClear={() => onChange(undefined)} />
    </Stack>
  );
};

export type DateRange = { from: string | null; to: string | null };

/** What a date range filter has set: whole days, either end open. */
export type DateRangeSelection = DateRange & { includeUnknown: boolean };

const backwards = ({ from, to }: DateRange) =>
  from !== null && to !== null && from > to;

/** A typed range that is neither backwards nor what the filter already holds. */
const unapplied = (
  range: DateRange,
  value: DateRangeSelection | undefined,
): boolean =>
  !backwards(range) &&
  (range.from !== (value?.from ?? null) || range.to !== (value?.to ?? null));

/**
 * A From and To date, either left open, and a tick to keep the rows with no
 * date at all, which a range on its own leaves out. A range that ends before
 * it starts is shown as such and never applied.
 */
export const DateRangeFilter = ({
  value,
  unknown,
  onChange,
}: {
  value: DateRangeSelection | undefined;
  unknown: { label: string; count: number };
  onChange: (value: DateRangeSelection | undefined) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const held: DateRange = { from: value?.from ?? null, to: value?.to ?? null };
  const [range, setRange] = useState<DateRange>(held);
  const includeUnknown = value?.includeUnknown ?? false;

  // A filter changed or removed from outside, by a chip or Clear all, replaces
  // whatever was typed, so a stale range is never applied again.
  const [lastHeld, setLastHeld] = useState(held);
  if (held.from !== lastHeld.from || held.to !== lastHeld.to) {
    setLastHeld(held);
    setRange(held);
  }

  // Typed dates settle before they apply, so a half-typed one is never asked
  // about; ticks apply at once.
  useEffect(() => {
    if (!unapplied(range, value)) {
      return;
    }

    const timer = setTimeout(() => {
      void onChange({ ...range, includeUnknown });
    }, DATE_SETTLE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [range, value, includeUnknown, onChange]);

  // The panel closes by unmounting, which would drop a range still settling.
  const latest = useRef({ range, value, includeUnknown, onChange });
  useEffect(() => {
    latest.current = { range, value, includeUnknown, onChange };
  });
  useEffect(
    () => () => {
      const pending = latest.current;

      if (unapplied(pending.range, pending.value)) {
        void pending.onChange({
          ...pending.range,
          includeUnknown: pending.includeUnknown,
        });
      }
    },
    [],
  );

  const dateField = (end: keyof DateRange) => (
    <TextField
      size="small"
      type="date"
      label={t(`listingFilters.${end}`)}
      value={range[end] ?? ""}
      onChange={(event) => {
        setRange({ ...range, [end]: event.target.value || null });
      }}
      error={backwards(range)}
      slotProps={{ inputLabel: { shrink: true } }}
    />
  );

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>
        {dateField("from")}
        {dateField("to")}
      </Stack>
      {backwards(range) ? (
        <Typography variant="body2" color="error" role="alert">
          {t("listingFilters.rangeBackwards")}
        </Typography>
      ) : null}
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={includeUnknown}
            onChange={(event) => {
              if (!backwards(range)) {
                void onChange({
                  ...range,
                  includeUnknown: event.target.checked,
                });
              }
            }}
          />
        }
        label={t("listingFilters.include", unknown)}
      />
      <ClearButton
        onClear={() => {
          setRange({ from: null, to: null });
          return onChange(undefined);
        }}
      />
    </Stack>
  );
};

const ClearButton = ({ onClear }: { onClear: () => Promisable<void> }) => {
  const { t } = useTranslation();

  return (
    <Button
      size="small"
      sx={{ alignSelf: "flex-start" }}
      onClick={() => {
        void onClear();
      }}
    >
      {t("listingFilters.clear")}
    </Button>
  );
};

/** One value a filter keeps, and how to take just that value out. */
export type FilterChip = {
  /** Unique among the chips shown together. */
  key: string;
  label: string;
  onRemove: () => Promisable<void>;
};

/** One chip per value a filter keeps, each removable, and a way to remove them all. */
export const FilterChips = ({
  chips,
  onClearAll,
}: {
  chips: FilterChip[];
  onClearAll: () => Promisable<void>;
}) => {
  const { t } = useTranslation();

  if (chips.length === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
    >
      {chips.map((chip) => (
        // The whole chip removes its value, so it is one control with one
        // name; MUI's delete icon alone is neither focusable nor named.
        <Chip
          key={chip.key}
          label={chip.label}
          aria-label={t("listingFilters.remove", { filter: chip.label })}
          onClick={() => {
            void chip.onRemove();
          }}
          onDelete={() => {
            void chip.onRemove();
          }}
          // A long name wraps inside its chip rather than running off a
          // narrow screen.
          sx={{
            height: "auto",
            minHeight: 32,
            maxWidth: "100%",
            "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
          }}
        />
      ))}
      <Button
        size="small"
        onClick={() => {
          void onClearAll();
        }}
      >
        {t("listingFilters.clearAll")}
      </Button>
    </Stack>
  );
};
